import React from 'react';
import styled from "styled-components";
import {useIntl, defineMessages} from "react-intl";
import dayjs from "dayjs";

const TrialDaysCounterMessages = defineMessages({
  daysRemaining: {
    id: "trialDaysCounter.daysRemaining",
    defaultMessage: "{days} day{plural} remaining",
  },
  expiresToday: {
    id: "trialDaysCounter.expiresToday",
    defaultMessage: "Expires today",
  },
  expired: {
    id: "trialDaysCounter.expired",
    defaultMessage: "Expired",
  },
});

const CounterContainer = styled.div<{isExpiring: boolean; isExpired: boolean}>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => {
    if (props.isExpired) return '#ff4d4f';
    if (props.isExpiring) return '#faad14';
    return '#f0f0f0';
  }};
  color: ${props => {
    if (props.isExpired) return '#ffffff';
    if (props.isExpiring) return '#ffffff';
    return '#595959';
  }};
`;

interface ITrialDaysCounter {
  trialEndAt?: string;
  gracePeriodUntil?: string;
}

export const TrialDaysCounter: React.FC<ITrialDaysCounter> = ({
  trialEndAt,
  gracePeriodUntil,
}) => {
  const intl = useIntl();

  // If grace period exists, show grace period days
  if (gracePeriodUntil) {
    const gracePeriodEnd = dayjs(gracePeriodUntil);
    const now = dayjs();
    const daysRemaining = Math.max(0, Math.ceil(gracePeriodEnd.diff(now, 'day', true)));

    if (daysRemaining <= 0) {
      return (
        <CounterContainer isExpired={true} isExpiring={false}>
          {intl.formatMessage(TrialDaysCounterMessages.expired)}
        </CounterContainer>
      );
    }

    return (
      <CounterContainer isExpiring={daysRemaining <= 3} isExpired={false}>
        {intl.formatMessage(TrialDaysCounterMessages.daysRemaining, {
          days: daysRemaining,
          plural: daysRemaining !== 1 ? 's' : '',
        })}
      </CounterContainer>
    );
  }

  // Otherwise show trial days
  if (!trialEndAt) {
    return null;
  }

  const trialEnd = dayjs(trialEndAt);
  const now = dayjs();
  const daysRemaining = Math.max(0, Math.ceil(trialEnd.diff(now, 'day', true)));

  if (daysRemaining === 0) {
    return (
      <CounterContainer isExpiring={true} isExpired={false}>
        {intl.formatMessage(TrialDaysCounterMessages.expiresToday)}
      </CounterContainer>
    );
  }

  if (daysRemaining < 0) {
    return (
      <CounterContainer isExpired={true} isExpiring={false}>
        {intl.formatMessage(TrialDaysCounterMessages.expired)}
      </CounterContainer>
    );
  }

  return (
    <CounterContainer isExpiring={daysRemaining <= 3} isExpired={false}>
      {intl.formatMessage(TrialDaysCounterMessages.daysRemaining, {
        days: daysRemaining,
        plural: daysRemaining !== 1 ? 's' : '',
      })}
    </CounterContainer>
  );
};
